"use client";

import React from "react";
import Link from "next/link";
import { ArrowUpRight, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { Container } from "@/components/ui/container";
import { TextGradient } from "@/components/ui/text-gradient";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

export function Footer() {
  return (
    <footer className="bg-gray-50 border-t border-gray-100 pt-16 pb-8">
      <Container>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <Link href="/" className="flex items-center mb-6">
              <div className="h-10 w-10 bg-blue-medium rounded-md flex items-center justify-center mr-2 shadow-md">
                <span className="font-bold text-white text-lg">BLM</span>
              </div>
              <span className="font-heading font-bold text-xl text-blue-medium">
                BlueLegos<span className="text-blue-dark">Matter</span>
              </span>
            </Link>
            <p className="text-gray-600 mb-6 max-w-md">
              Advocating for equality and appreciation of blue Lego bricks around the world. Every blue brick deserves to be part of something amazing.
            </p>
            <div className="flex space-x-4">
              <motion.a 
                href="#" 
                className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center text-blue-dark hover:bg-blue-medium hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Facebook size={18} />
              </motion.a>
              <motion.a 
                href="#" 
                className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center text-blue-dark hover:bg-blue-medium hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Twitter size={18} />
              </motion.a>
              <motion.a 
                href="#" 
                className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center text-blue-dark hover:bg-blue-medium hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Instagram size={18} />
              </motion.a>
              <motion.a 
                href="#" 
                className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center text-blue-dark hover:bg-blue-medium hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Linkedin size={18} />
              </motion.a>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Organization</h3>
            <ul className="space-y-3">
              <li>
                <Link href="#about" className="text-gray-600 hover:text-blue-medium transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="#mission" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Our Mission
                </Link>
              </li>
              <li>
                <Link href="#team" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Team
                </Link>
              </li>
              <li>
                <Link href="#careers" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Careers
                </Link>
              </li>
              <li>
                <Link href="#contact" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Initiatives</h3>
            <ul className="space-y-3">
              <li>
                <Link href="#build-blue" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Build Blue Program
                </Link>
              </li>
              <li>
                <Link href="#blue-rescue" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Blue Brick Rescue
                </Link>
              </li>
              <li>
                <Link href="#education" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Education Outreach
                </Link>
              </li>
              <li>
                <Link href="#advocacy" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Advocacy & Policy
                </Link>
              </li>
              <li>
                <Link href="#events" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Events
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Get Involved</h3>
            <ul className="space-y-3">
              <li>
                <Link href="#donate" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Donate
                </Link>
              </li>
              <li>
                <Link href="#volunteer" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Volunteer
                </Link>
              </li>
              <li>
                <Link href="#partner" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Partnership
                </Link>
              </li>
              <li>
                <Link href="#newsletter" className="text-gray-600 hover:text-blue-medium transition-colors">
                  Newsletter
                </Link>
              </li>
              <li>
                <Link href="/fundraise" className="inline-flex items-center text-blue-medium font-medium hover:text-blue-dark transition-colors">
                  Start a Fundraiser <ArrowUpRight className="ml-1 h-4 w-4" />
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              © 2024 Blue Legos Matter. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link href="/privacy" className="text-sm text-gray-500 hover:text-blue-medium transition-colors">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-sm text-gray-500 hover:text-blue-medium transition-colors">
                Terms of Service
              </Link>
              <Link href="/cookies" className="text-sm text-gray-500 hover:text-blue-medium transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
}